/**
 * gstReturns.ts — Pure aggregation of outward supplies into the structures
 * that drive GSTR-1 (section-wise) and the GSTR-3B outward summary.
 *
 * This is the single source of truth for how invoices roll up into GST
 * return sections. Both the on-screen report and the government JSON export
 * consume the same `GstReturnDataset` so the numbers can never diverge.
 *
 * All inputs are plain objects (decoupled from WatermelonDB) so the logic is
 * trivially unit-testable. Monetary values are rupees (matching the DB) and
 * every output figure is rounded to 2 decimals.
 */
import { isValidGstin, stateCodeFromGstin, stateCodeFromName, stateLabel } from './gstStates';
import { isValidGSTRate } from './gstRates';
/**
 * Unregistered B2C credit note ≥ this value (inter-state) files under cdnur/B2CL.
 *
 * Reduced from ₹2,50,000 to ₹1,00,000 by Notification 12/2024-Central Tax.
 * Erring low is the safe direction: it files *more* notes invoice-wise, which the
 * portal accepts, whereas erring high under-reports and is a defect.
 */
export const B2CL_THRESHOLD = 100000;
/**
 * Warning codes that make a GSTR-1 JSON un-fileable. The portal rejects the
 * upload outright (an empty `hsn_sc`, a non-slab rate, or a missing supplier
 * GSTIN), so the export must be blocked rather than producing a file that
 * bounces. `INVALID_GSTIN` is *not* blocking — such an invoice legitimately
 * files as B2C.
 */
export const GSTR1_BLOCKING_WARNING_CODES = [
    'MISSING_HSN',
    'INVALID_RATE',
    'MISSING_SELLER_GSTIN',
    // Header ≠ lines. GSTR-1 is built from the lines and GSTR-3B from the header,
    // so filing both would declare two different totals for the same invoice.
    'RATE_MISMATCH',
    // The portal rejects a row whose place of supply differs from the supplier's
    // state while carrying CGST/SGST (and vice versa).
    'POS_TAX_MISMATCH',
];
/** Warnings that must be cleared before the GSTR-1 JSON can be exported. */
export function blockingWarnings(ds) {
    return ds.warnings.filter((w) => GSTR1_BLOCKING_WARNING_CODES.includes(w.code));
}
// ─── Helpers ─────────────────────────────────────────────────────────────────
export function round2(n) {
    // Guard against −0 and floating dust before fixing to 2 dp.
    return Math.round((n + Number.EPSILON) * 100) / 100;
}
/** Map a free-text unit to a GST Unit Quantity Code (UQC). */
export function toUqc(unit) {
    var _a;
    if (!unit)
        return 'OTH';
    const u = unit.trim().toLowerCase();
    const map = {
        pcs: 'PCS', pc: 'PCS', piece: 'PCS', pieces: 'PCS', nos: 'NOS', no: 'NOS', unit: 'NOS', units: 'NOS',
        kg: 'KGS', kgs: 'KGS', kilogram: 'KGS', g: 'GMS', gm: 'GMS', gms: 'GMS', gram: 'GMS',
        l: 'LTR', ltr: 'LTR', litre: 'LTR', liter: 'LTR', ml: 'MLT',
        box: 'BOX', bag: 'BAG', bottle: 'BTL', btl: 'BTL', pkt: 'PAC', packet: 'PAC', pack: 'PAC',
        dozen: 'DOZ', doz: 'DOZ', pair: 'PRS', prs: 'PRS', mtr: 'MTR', meter: 'MTR', metre: 'MTR',
        set: 'SET', roll: 'ROL', can: 'CAN', tin: 'CAN', sqft: 'SQF', sqm: 'SQM',
    };
    return (_a = map[u]) !== null && _a !== void 0 ? _a : 'OTH';
}
/** Resolve the place-of-supply state code for an invoice. */
function resolvePos(inv, homeStateCode) {
    const c = inv.customer;
    if (c) {
        const fromGstin = isValidGstin(c.gstin) ? stateCodeFromGstin(c.gstin) : null;
        if (fromGstin)
            return fromGstin;
        const fromState = stateCodeFromName(c.state);
        if (fromState)
            return fromState;
    }
    return homeStateCode;
}
/**
 * Largest gap tolerated between an invoice's header tax and the sum of its lines.
 * One paisa per line is normal rounding; anything more means the two disagree.
 */
const HEADER_LINE_TOLERANCE = 0.05;
/**
 * Assert that an invoice's header tax equals the sum of its lines.
 *
 * `RATE_MISMATCH` was declared in the warning union and raised nowhere. Every
 * return section is built from the *lines*, while the 3B outward summary and the
 * B2B invoice value come from the *header* — so if a sync merge, an edit, or a
 * void ever leaves the two inconsistent, the return silently reports two
 * different totals for the same invoice. This is the tripwire for that.
 */
function checkHeaderMatchesLines(inv, warnings) {
    const sum = (pick) => inv.lines.reduce((s, li) => s + pick(li), 0);
    const checks = [
        ['taxable value', inv.taxableAmount, sum((li) => li.taxableAmount)],
        ['CGST', inv.cgstAmount, sum((li) => li.cgstAmount)],
        ['SGST', inv.sgstAmount, sum((li) => li.sgstAmount)],
        ['IGST', inv.igstAmount, sum((li) => li.igstAmount)],
        ['cess', inv.cessAmount, sum((li) => li.cessAmount)],
    ];
    for (const [label, header, lines] of checks) {
        const diff = round2(header - lines);
        if (Math.abs(diff) > HEADER_LINE_TOLERANCE) {
            warnings.push({
                code: 'RATE_MISMATCH',
                invoiceNumber: inv.invoiceNumber,
                detail: `Invoice ${label} is ${round2(header)} but its lines total ${round2(lines)} (off by ${diff}).`,
            });
        }
    }
}
/**
 * The tax heads charged must agree with the place of supply.
 *
 * Inter-state supplies carry IGST; intra-state supplies carry CGST+SGST. Desktop
 * decides this from a cashier-facing checkbox rather than from the customer's
 * GSTIN, so forgetting to tick it charges the wrong government — the buyer's
 * input credit is at risk, and the portal rejects the row outright.
 *
 * Zero-rated lines carry no tax at all, so nothing can be inferred from the
 * components; those are skipped rather than guessed at.
 */
function checkPosMatchesTaxHeads(inv, pos, home, warnings) {
    const hasIgst = inv.igstAmount > 0;
    const hasCgstSgst = inv.cgstAmount > 0 || inv.sgstAmount > 0;
    if (!hasIgst && !hasCgstSgst)
        return; // nil / exempt / zero-rated — nothing to check
    if (!pos || !home)
        return;
    const shouldBeInterState = pos !== home;
    if (shouldBeInterState && hasCgstSgst) {
        warnings.push({
            code: 'POS_TAX_MISMATCH',
            invoiceNumber: inv.invoiceNumber,
            detail: `Place of supply is ${stateLabel(pos)} but the invoice charges CGST/SGST — an inter-state supply must charge IGST.`,
        });
    }
    else if (!shouldBeInterState && hasIgst) {
        warnings.push({
            code: 'POS_TAX_MISMATCH',
            invoiceNumber: inv.invoiceNumber,
            detail: `Place of supply is ${stateLabel(pos)} (your own state) but the invoice charges IGST — an intra-state supply must charge CGST/SGST.`,
        });
    }
}
/**
 * HSN prefixes for supplies OUTSIDE the GST net — petroleum and alcohol. A
 * rate-0 line with one of these reports as "non-GST" rather than "exempt".
 * Everything else rate-0 defaults to exempt, the common retail case (books
 * HSN 4901, unbranded food, etc.). The list is minimal and additive; we cannot
 * reliably separate nil-rated from exempt without more product data, so nil-rated
 * folds into exempt — the safe direction for an outward-supply summary.
 */
const NON_GST_HSN_PREFIXES = ['2709', '2710', '2711', '2203', '2204', '2205', '2206', '2207', '2208'];
/** How a rate-0 line reports in Table 8. */
function classifyNilExempt(hsn) {
    const code = (hsn !== null && hsn !== void 0 ? hsn : '').trim();
    if (NON_GST_HSN_PREFIXES.some((p) => code.startsWith(p)))
        return 'nonGst';
    return 'exempt';
}
/** A rate-0 supply carries no GST — it belongs in Table 8, not the taxable tables. */
function isNilExemptLine(li) {
    return li.gstRate === 0;
}
/** Split an invoice's line taxable-values into taxable vs the three Table-8 kinds. */
function splitLineValues(lines) {
    const s = { taxable: 0, nilRated: 0, exempt: 0, nonGst: 0 };
    for (const li of lines) {
        if (isNilExemptLine(li))
            s[classifyNilExempt(li.hsnCode)] += li.taxableAmount;
        else
            s.taxable += li.taxableAmount;
    }
    return s;
}
function nilCategory(isInterState, isRegistered) {
    if (isInterState)
        return isRegistered ? 'INTER_REG' : 'INTER_UNREG';
    return isRegistered ? 'INTRA_REG' : 'INTRA_UNREG';
}
/**
 * Sum an invoice's TAXABLE lines into unsigned rate-wise buckets (for b2b / cdnr
 * items). Nil/exempt (rate-0) lines are excluded — they report in Table 8, and
 * folding them here would put a rate-0 row into B2B/B2CS where the portal expects
 * only taxable supplies.
 */
function computeRateBuckets(inv) {
    var _a;
    const m = new Map();
    for (const li of inv.lines) {
        if (isNilExemptLine(li))
            continue;
        const rate = li.gstRate;
        const b = (_a = m.get(rate)) !== null && _a !== void 0 ? _a : { rate, taxableValue: 0, igst: 0, cgst: 0, sgst: 0, cess: 0 };
        b.taxableValue += li.taxableAmount;
        b.igst += li.igstAmount;
        b.cgst += li.cgstAmount;
        b.sgst += li.sgstAmount;
        b.cess += li.cessAmount;
        m.set(rate, b);
    }
    return m;
}
function roundBucket(b) {
    return {
        rate: b.rate,
        taxableValue: round2(b.taxableValue),
        igst: round2(b.igst),
        cgst: round2(b.cgst),
        sgst: round2(b.sgst),
        cess: round2(b.cess),
    };
}
/**
 * Apply an invoice's lines to the HSN summary with a sign (+1 for sales, −1 for
 * credit notes) so table 12 is net. Only sales lines raise a MISSING_HSN
 * warning (avoids double-counting the same underlying item).
 */
function applyHsn(hsnMap, inv, sign, warnings) {
    var _a, _b;
    for (const li of inv.lines) {
        if (!li.hsnCode && sign > 0) {
            warnings.push({
                code: 'MISSING_HSN',
                invoiceNumber: inv.invoiceNumber,
                detail: `Line "${(_a = li.description) !== null && _a !== void 0 ? _a : 'item'}" has no HSN/SAC code.`,
            });
        }
        const rate = li.gstRate;
        const key = `${li.hsnCode || 'NO_HSN'}|${rate}`;
        const h = (_b = hsnMap.get(key)) !== null && _b !== void 0 ? _b : {
            hsn: li.hsnCode || '',
            description: li.description || '',
            uqc: toUqc(li.unit),
            quantity: 0, taxableValue: 0, igst: 0, cgst: 0, sgst: 0, cess: 0,
            rate, totalValue: 0,
        };
        h.quantity += sign * li.quantity;
        h.taxableValue += sign * li.taxableAmount;
        h.igst += sign * li.igstAmount;
        h.cgst += sign * li.cgstAmount;
        h.sgst += sign * li.sgstAmount;
        h.cess += sign * li.cessAmount;
        h.totalValue += sign * li.totalAmount;
        hsnMap.set(key, h);
    }
}
/** Add or subtract a set of rate buckets into the b2cs map (net small B2C). */
function applyB2cs(b2csMap, buckets, supplyType, pos, sign) {
    var _a;
    for (const b of buckets) {
        const key = `${supplyType}|${pos}|${b.rate}`;
        const row = (_a = b2csMap.get(key)) !== null && _a !== void 0 ? _a : {
            supplyType, posCode: pos, rate: b.rate,
            taxableValue: 0, igst: 0, cgst: 0, sgst: 0, cess: 0,
        };
        row.taxableValue += sign * b.taxableValue;
        row.igst += sign * b.igst;
        row.cgst += sign * b.cgst;
        row.sgst += sign * b.sgst;
        row.cess += sign * b.cess;
        b2csMap.set(key, row);
    }
}
// ─── Core aggregation ────────────────────────────────────────────────────────
/**
 * Roll up a set of invoices (SALE + CREDIT_NOTE) into GST return sections.
 * Only SALE invoices populate b2b/b2cs/hsn; the 3B outward figure is net of
 * credit notes so it matches the return that tax is actually paid on.
 */
export function buildGstReturns(invoices, opts) {
    var _a, _b, _c, _d, _e, _f;
    const home = opts.homeStateCode;
    // Cancelled (voided) invoices are excluded from every value aggregation but
    // still counted in Table 13 — the serial was issued and the portal must see it.
    const active = invoices.filter((i) => !i.isCancelled);
    const sales = active.filter((i) => i.type === 'SALE');
    const creditNotes = active.filter((i) => i.type === 'CREDIT_NOTE');
    const warnings = [];
    // Return-level check: you cannot file GSTR-1 without a valid seller GSTIN.
    if ('sellerGstin' in opts && !isValidGstin(opts.sellerGstin)) {
        warnings.push({
            code: 'MISSING_SELLER_GSTIN',
            invoiceNumber: '',
            detail: opts.sellerGstin
                ? `Your store GSTIN "${opts.sellerGstin}" is malformed — fix it in Settings before filing.`
                : 'Your store has no GSTIN set — add it in Settings to file GSTR-1.',
        });
    }
    // Track invalid GST slabs once per invoice+rate so the panel isn't spammed.
    const seenInvalidRate = new Set();
    // ── B2B: group by customer GSTIN → invoice → rate ──
    const b2bMap = new Map();
    // ── B2CL (Table 5): large inter-state B2C sales, invoice-wise, grouped by POS ──
    const b2clMap = new Map();
    // ── B2CS: group by (supplyType, pos, rate); net of small B2C credit notes ──
    const b2csMap = new Map();
    // ── Table 8: nil/exempt/non-GST, by category; net of credit notes ──
    const nilMap = new Map();
    const applyNil = (split, cat, sign) => {
        var _a;
        if (split.nilRated + split.exempt + split.nonGst === 0)
            return;
        const row = (_a = nilMap.get(cat)) !== null && _a !== void 0 ? _a : { nilRated: 0, exempt: 0, nonGst: 0 };
        row.nilRated += sign * split.nilRated;
        row.exempt += sign * split.exempt;
        row.nonGst += sign * split.nonGst;
        nilMap.set(cat, row);
    };
    // ── HSN: group by (hsn, rate); net of credit notes ──
    const hsnMap = new Map();
    // ── CDNR / CDNUR: credit notes to registered / unregistered-B2CL ──
    const cdnrMap = new Map();
    const cdnur = [];
    // Outward sales → b2b / b2cs / hsn(+)
    for (const inv of sales) {
        const hasGstin = isValidGstin((_a = inv.customer) === null || _a === void 0 ? void 0 : _a.gstin);
        if (((_b = inv.customer) === null || _b === void 0 ? void 0 : _b.gstin) && !hasGstin) {
            warnings.push({
                code: 'INVALID_GSTIN',
                invoiceNumber: inv.invoiceNumber,
                detail: `GSTIN "${inv.customer.gstin}" is malformed — invoice will file as B2C.`,
            });
        }
        const pos = resolvePos(inv, home);
        const supplyType = inv.igstAmount > 0 ? 'INTER' : 'INTRA';
        const rateBuckets = computeRateBuckets(inv);
        applyHsn(hsnMap, inv, 1, warnings);
        checkHeaderMatchesLines(inv, warnings);
        checkPosMatchesTaxHeads(inv, pos, home, warnings);
        // Flag any GST rate that isn't a recognised slab (e.g. a typo like 15%).
        for (const rate of rateBuckets.keys()) {
            const key = `${inv.invoiceNumber}|${rate}`;
            if (!isValidGSTRate(rate) && !seenInvalidRate.has(key)) {
                seenInvalidRate.add(key);
                warnings.push({
                    code: 'INVALID_RATE',
                    invoiceNumber: inv.invoiceNumber,
                    detail: `GST rate ${rate}% is not a valid slab (0/0.25/3/5/12/18/28…).`,
                });
            }
        }
        // Nil/exempt/non-GST lines → Table 8, keyed by inter/intra (from PoS, since
        // an exempt supply carries no IGST to infer it from) × registered/unregistered.
        applyNil(splitLineValues(inv.lines), nilCategory(pos !== home, hasGstin), 1);
        // Only the taxable lines feed b2b/b2cl/b2cs. A wholly nil/exempt invoice has
        // no rate buckets and belongs solely in Table 8 — don't create an empty row.
        if (rateBuckets.size === 0)
            continue;
        if (hasGstin) {
            const ctin = inv.customer.gstin.trim().toUpperCase();
            const party = (_c = b2bMap.get(ctin)) !== null && _c !== void 0 ? _c : { ctin, customerName: inv.customer.name || 'Customer', invoices: [] };
            party.invoices.push({
                invoiceNumber: inv.invoiceNumber,
                date: inv.invoiceDate,
                invoiceValue: round2(inv.totalAmount),
                posCode: pos,
                reverseCharge: false,
                items: [...rateBuckets.values()].map(roundBucket),
            });
            b2bMap.set(ctin, party);
        }
        else if (supplyType === 'INTER' && inv.totalAmount >= B2CL_THRESHOLD) {
            // Large inter-state B2C sale → Table 5 (B2CL), reported invoice-wise.
            // Previously these were folded into B2CS, which the portal treats as a
            // separate table — the invoices simply went unreported there.
            const b2cl = (_d = b2clMap.get(pos)) !== null && _d !== void 0 ? _d : { posCode: pos, invoices: [] };
            b2cl.invoices.push({
                invoiceNumber: inv.invoiceNumber,
                date: inv.invoiceDate,
                invoiceValue: round2(inv.totalAmount),
                items: [...rateBuckets.values()].map(roundBucket),
            });
            b2clMap.set(pos, b2cl);
        }
        else {
            applyB2cs(b2csMap, rateBuckets.values(), supplyType, pos, 1);
        }
    }
    // Credit notes → cdnr / cdnur / b2cs(−); hsn(−) in every case (net table 12)
    for (const cn of creditNotes) {
        const hasGstin = isValidGstin((_e = cn.customer) === null || _e === void 0 ? void 0 : _e.gstin);
        const pos = resolvePos(cn, home);
        const supplyType = cn.igstAmount > 0 ? 'INTER' : 'INTRA';
        const rateBuckets = computeRateBuckets(cn);
        applyHsn(hsnMap, cn, -1, warnings);
        checkHeaderMatchesLines(cn, warnings);
        checkPosMatchesTaxHeads(cn, pos, home, warnings);
        // Net any nil/exempt lines out of Table 8.
        applyNil(splitLineValues(cn.lines), nilCategory(pos !== home, hasGstin), -1);
        // A wholly nil/exempt credit note has no taxable buckets — it only reduces
        // Table 8 above and must not create an empty cdnr/cdnur/b2cs entry.
        if (rateBuckets.size === 0)
            continue;
        if (hasGstin) {
            const ctin = cn.customer.gstin.trim().toUpperCase();
            const party = (_f = cdnrMap.get(ctin)) !== null && _f !== void 0 ? _f : { ctin, customerName: cn.customer.name || 'Customer', notes: [] };
            party.notes.push({
                noteType: 'C',
                noteNumber: cn.invoiceNumber,
                date: cn.invoiceDate,
                value: round2(cn.totalAmount),
                posCode: pos,
                reverseCharge: false,
                items: [...rateBuckets.values()].map(roundBucket),
            });
            cdnrMap.set(ctin, party);
        }
        else if (supplyType === 'INTER' && cn.totalAmount >= B2CL_THRESHOLD) {
            // Large inter-state B2C credit note → cdnur (B2CL).
            cdnur.push({
                supplyType: 'B2CL',
                noteType: 'C',
                noteNumber: cn.invoiceNumber,
                date: cn.invoiceDate,
                value: round2(cn.totalAmount),
                posCode: pos,
                items: [...rateBuckets.values()].map(roundBucket),
            });
        }
        else {
            // Small B2C credit note → net out of b2cs.
            applyB2cs(b2csMap, rateBuckets.values(), supplyType, pos, -1);
        }
    }
    // ── 3B outward = net (sales − credit notes) ──
    // taxableValue is 3.1(a) — taxable supplies only. Exempt-line value is excluded
    // here and reported separately as 3.1(c); the tax components are unchanged since
    // exempt lines carry no tax.
    const acc = { taxableValue: 0, igst: 0, cgst: 0, sgst: 0, cess: 0, totalTax: 0, invoiceValue: 0 };
    for (const inv of sales) {
        acc.taxableValue += splitLineValues(inv.lines).taxable;
        acc.igst += inv.igstAmount;
        acc.cgst += inv.cgstAmount;
        acc.sgst += inv.sgstAmount;
        acc.cess += inv.cessAmount;
        acc.totalTax += inv.totalTax;
        acc.invoiceValue += inv.totalAmount;
    }
    const net = { taxableValue: acc.taxableValue, igst: acc.igst, cgst: acc.cgst, sgst: acc.sgst, cess: acc.cess };
    for (const cn of creditNotes) {
        net.taxableValue -= splitLineValues(cn.lines).taxable;
        net.igst -= cn.igstAmount;
        net.cgst -= cn.cgstAmount;
        net.sgst -= cn.sgstAmount;
        net.cess -= cn.cessAmount;
    }
    // 3.1(c) other outward supplies (nil/exempt) — net of credit notes, from the
    // Table 8 accumulator. Clamped at zero like 3.1(a).
    const nilExemptNet = [...nilMap.values()].reduce((s, r) => s + r.nilRated + r.exempt + r.nonGst, 0);
    const nilExemptSupplies = round2(Math.max(0, nilExemptNet));
    // The portal floors 3.1(a) at zero; a negative net (credit notes > sales this
    // period) carries to the next return. Clamp each component and remember what
    // was clamped away so the UI can show the carry-forward rather than hide it.
    const clampField = (v) => v < 0 ? { value: 0, carry: round2(-v) } : { value: round2(v), carry: 0 };
    const c = {
        taxableValue: clampField(net.taxableValue),
        igst: clampField(net.igst),
        cgst: clampField(net.cgst),
        sgst: clampField(net.sgst),
        cess: clampField(net.cess),
    };
    const carryForward = {
        taxableValue: c.taxableValue.carry,
        igst: c.igst.carry,
        cgst: c.cgst.carry,
        sgst: c.sgst.carry,
        cess: c.cess.carry,
    };
    if (Object.values(carryForward).some((v) => v > 0)) {
        warnings.push({
            code: 'NEGATIVE_3B_CARRY_FORWARD',
            invoiceNumber: '',
            detail: 'Credit notes exceed sales this period, so outward tax is negative. ' +
                'GSTR-3B 3.1(a) is floored at zero and the excess carries to the next return.',
        });
    }
    // ── Doc issued summary (Table 13): all invoices incl. cancelled, per series ──
    const docIssued = buildDocIssued(invoices);
    const roundedB2cs = [...b2csMap.values()]
        .map((r) => ({
        ...r,
        taxableValue: round2(r.taxableValue),
        igst: round2(r.igst),
        cgst: round2(r.cgst),
        sgst: round2(r.sgst),
        cess: round2(r.cess),
    }))
        .sort((a, b) => a.posCode.localeCompare(b.posCode) || a.rate - b.rate);
    const roundedHsn = [...hsnMap.values()]
        .map((r) => ({
        ...r,
        quantity: round2(r.quantity),
        taxableValue: round2(r.taxableValue),
        igst: round2(r.igst),
        cgst: round2(r.cgst),
        sgst: round2(r.sgst),
        cess: round2(r.cess),
        totalValue: round2(r.totalValue),
    }))
        .sort((a, b) => b.taxableValue - a.taxableValue);
    const cdnrParties = [...cdnrMap.values()].sort((a, b) => a.ctin.localeCompare(b.ctin));
    const cdnrNoteCount = cdnrParties.reduce((s, p) => s + p.notes.length, 0);
    const b2clRows = [...b2clMap.values()]
        .map((p) => ({
        ...p,
        invoices: [...p.invoices].sort((a, b) => a.invoiceNumber.localeCompare(b.invoiceNumber, undefined, { numeric: true })),
    }))
        .sort((a, b) => a.posCode.localeCompare(b.posCode));
    const b2clInvoiceCount = b2clRows.reduce((s, p) => s + p.invoices.length, 0);
    const CATEGORY_ORDER = ['INTER_REG', 'INTRA_REG', 'INTER_UNREG', 'INTRA_UNREG'];
    const nilExempt = CATEGORY_ORDER
        .filter((cat) => nilMap.has(cat))
        .map((cat) => {
        const r = nilMap.get(cat);
        return {
            category: cat,
            nilRated: round2(r.nilRated),
            exempt: round2(r.exempt),
            nonGst: round2(r.nonGst),
        };
    })
        // Drop categories that fully netted to nothing (all zero after credit notes).
        .filter((r) => r.nilRated !== 0 || r.exempt !== 0 || r.nonGst !== 0);
    return {
        b2b: [...b2bMap.values()].sort((a, b) => a.ctin.localeCompare(b.ctin)),
        b2cs: roundedB2cs,
        b2cl: b2clRows,
        nilExempt,
        cdnr: cdnrParties,
        cdnur: cdnur.sort((a, b) => a.noteNumber.localeCompare(b.noteNumber, undefined, { numeric: true })),
        hsn: roundedHsn,
        docIssued,
        gstr3b: {
            taxableValue: c.taxableValue.value,
            igst: c.igst.value,
            cgst: c.cgst.value,
            sgst: c.sgst.value,
            cess: c.cess.value,
            nilExemptSupplies,
            carryForward,
        },
        totals: {
            saleInvoiceCount: sales.length,
            creditNoteCount: creditNotes.length,
            b2bInvoiceCount: [...b2bMap.values()].reduce((s, p) => s + p.invoices.length, 0),
            b2clInvoiceCount,
            cdnrNoteCount,
            cdnurNoteCount: cdnur.length,
            taxableValue: round2(acc.taxableValue),
            igst: round2(acc.igst),
            cgst: round2(acc.cgst),
            sgst: round2(acc.sgst),
            cess: round2(acc.cess),
            totalTax: round2(acc.totalTax),
            invoiceValue: round2(acc.invoiceValue),
        },
        warnings,
    };
}
/**
 * Split an invoice number into its numbering series and sequence, given the
 * `{prefix}-{slot2}{seq6}` format (e.g. `INV-01000007` → series `INV-01`, seq 7).
 *
 * Each device slot mints its own series, so the series must include the slot —
 * reporting a single min→max across slots (as the old stub did) makes the count
 * disagree with the numeric span and the portal/CA queries the gap. A legacy
 * number with a bare 6-digit sequence (`INV-000007`) has no slot and keeps the
 * whole prefix as its series.
 */
function parseDocSeries(invoiceNumber) {
    const m = invoiceNumber.match(/^(.*?)(\d+)$/);
    if (!m)
        return { series: invoiceNumber, seq: 0 };
    const [, prefix, digits] = m;
    if (digits.length >= 8) {
        const slot = digits.slice(0, digits.length - 6);
        return { series: prefix + slot, seq: Number(digits.slice(digits.length - 6)) };
    }
    return { series: prefix, seq: Number(digits) };
}
/**
 * Build the GSTR-1 "Documents Issued" summary (Table 13).
 *
 * One entry per (document type, numbering series). Invoices and credit notes are
 * separate document natures on the portal, and each device slot is a separate
 * series. Cancelled (voided) documents are counted here — the serial was issued,
 * so the portal must see it as cancelled rather than as a missing number.
 */
function buildDocIssued(invoices) {
    var _a;
    const groups = new Map();
    for (const inv of invoices) {
        const docType = inv.type === 'CREDIT_NOTE' ? 'CREDIT_NOTE' : 'INVOICE';
        const { series } = parseDocSeries(inv.invoiceNumber);
        const key = `${docType}|${series}`;
        const g = (_a = groups.get(key)) !== null && _a !== void 0 ? _a : { docType, series, docs: [] };
        g.docs.push(inv);
        groups.set(key, g);
    }
    return [...groups.values()]
        .map(({ docType, series, docs }) => {
        const sorted = [...docs].sort((a, b) => parseDocSeries(a.invoiceNumber).seq - parseDocSeries(b.invoiceNumber).seq);
        return {
            docType,
            series,
            from: sorted[0].invoiceNumber,
            to: sorted[sorted.length - 1].invoiceNumber,
            totalNumber: sorted.length,
            cancelled: sorted.filter((d) => d.isCancelled).length,
        };
    })
        .sort((a, b) => a.docType.localeCompare(b.docType) || a.series.localeCompare(b.series));
}
